import React, { useMemo } from 'react'

import {
  Button,
  Fieldset,
  Input,
  ModalBox,
  Select,
  type SelectOption,
  useModalContext,
} from '@fourthwall/components'
import { useFormik } from '@fourthwall/utils/dist/formik'
import { Tag } from '@modules/Dashboard/Memberships/components/Tag'

import * as S from './ConfigureWebhookModal.styled'
import type { Values } from './ConfigureWebhookModal.types'
import { validationSchema } from './consts'

export type ConfigureWebhookModalProps = {
  initialValues?: Values
  eventOptions: SelectOption[]
  onSubmit: (values: Values) => void
}

export const ConfigureWebhookModal = ({
  initialValues,
  eventOptions,
  onSubmit,
}: ConfigureWebhookModalProps) => {
  const { close } = useModalContext()

  const formik = useFormik<Values>({
    initialValues: {
      url: initialValues?.url || '',
      events: initialValues?.events || [],
    },
    validationSchema,
    onSubmit: (values) => {
      close()
      onSubmit(values)
    },
  })

  const selectOptions = useMemo(() => {
    return eventOptions.filter((option) => !formik.values.events.includes(option.value))
  }, [eventOptions, formik.values.events])

  const handleAddEvent = (event: string) => {
    const activeElement = window.document.activeElement as HTMLElement | null
    activeElement?.blur()

    formik.setFieldValue('events', [...formik.values.events, event])
  }

  const handleRemoveEvent = (event: string) => {
    formik.setFieldValue(
      'events',
      formik.values.events.filter((existingEvent) => existingEvent !== event),
    )
  }

  const getTagLabel = (event: string) => {
    return eventOptions.find((option) => option.value === event)?.label || ''
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <ModalBox
        title="Create webhook"
        size="small"
        buttons={[
          <Button label="Cancel" appearance="secondary" onClick={close} />,
          <Button label="Save" appearance="primary" type="submit" />,
        ]}
        onClose={close}
      >
        <Fieldset>
          <Fieldset.Row>
            <Fieldset.Item flex={1}>
              <Input label="URL" {...formik.getFieldProps('url')} />
            </Fieldset.Item>
          </Fieldset.Row>
          <Fieldset.Row>
            <Fieldset.Item flex={1}>
              <Select
                label="Add event"
                value=""
                disabled={!selectOptions.length}
                options={selectOptions}
                error={formik.getFieldProps('events').error}
                onValueChange={handleAddEvent}
              />
            </Fieldset.Item>
          </Fieldset.Row>
          {formik.values.events.length > 0 && (
            <S.EventsContainer>
              {formik.values.events.map((event) => (
                <Tag
                  key={event}
                  label={getTagLabel(event)}
                  onRemove={() => handleRemoveEvent(event)}
                />
              ))}
            </S.EventsContainer>
          )}
        </Fieldset>
      </ModalBox>
    </form>
  )
}
